import { config } from "../config";

export type SupportRole = "assistant" | "user";
export type EmergencyLanguage = "en" | "es" | "nl";

export type SupportEvent =
  | { action: "init"; caseId: string; language: EmergencyLanguage }
  | { action: "message"; caseId: string; text: string; language: EmergencyLanguage }
  | { action: "report_sent"; caseId: string; reportId: string; language: EmergencyLanguage }
  | { action: "case_report"; caseId: string; language: EmergencyLanguage }
  | { action: "identity_set"; caseId: string; firstName: string; language: EmergencyLanguage }
  | {
      action: "detect_language";
      caseId: string;
      browserLanguages?: ReadonlyArray<string>;
      location?: { lat: number; lon: number };
    };

function fallbackReply(input: string, language: EmergencyLanguage) {
  const text = input.toLowerCase();
  if (language === "en") {
    if (text.includes("trapped") || text.includes("rubble") || text.includes("stuck")) {
      return "I hear you. Stay as still as you safely can, save your breath, and keep your mouth and nose as clear as possible. Right now your only job is to stay with me and breathe slowly.";
    }
    if (text.includes("thirst") || text.includes("thirsty") || text.includes("water") || text.includes("dry mouth") || text.includes("mouth is dry") || text.includes("sed")) {
      return "I hear you. If water is safely within reach, take a very small sip. If not, stay with me and keep your breathing slow while we handle the next small step.";
    }
    if (text.includes("can't breathe") || text.includes("cant breathe") || text.includes("cannot breathe") || text.includes("can't breath")) {
      return "I’m here with you. Keep your mouth and nose as clear as you safely can, take the tiniest breaths you can manage, and stay as still as you safely can.";
    }
    if (text.includes("panic") || text.includes("scared") || text.includes("anxious")) {
      return "Let’s slow this down together. Put one hand on your chest and one on your stomach. Inhale for 4, exhale for 6. You do not need to do anything else right now except stay with me.";
    }
    if (text.includes("blood") || text.includes("bleed")) {
      return "If you can reach it safely, apply steady pressure with clean cloth or fabric. Keep breathing slowly and stay focused on the next small thing.";
    }
    if (text.includes("family") || text.includes("loved") || text.includes("child") || text.includes("wife") || text.includes("husband")) {
      return "If you can think about loved ones safely, just picture who was with you last and keep that in mind. We are staying calm one breath at a time.";
    }
    return "I’m here with you. Keep your breathing slow, stay as still as you safely can, and use the buttons below for a steady prompt while you wait.";
  }

  if (language === "nl") {
    if (text.includes("trapped") || text.includes("rubble") || text.includes("stuck")) {
      return "Ik hoor je. Blijf zo stil mogelijk, bespaar je adem en houd je mond en neus zo vrij mogelijk. Het enige wat je nu hoeft te doen is bij mij blijven en rustig ademen.";
    }
    if (text.includes("dorst") || text.includes("water") || text.includes("droge mond")) {
      return "Ik hoor je. Als water veilig binnen handbereik is, neem dan een kleine slok. Zo niet, blijf bij mij en adem rustig terwijl we de volgende kleine stap nemen.";
    }
    if (text.includes("can't breathe") || text.includes("cant breathe") || text.includes("cannot breathe") || text.includes("can't breath")) {
      return "Ik ben hier bij je. Houd je mond en neus zo vrij mogelijk, neem zo kleine mogelijk ademhalingen en blijf zo stil als veilig is.";
    }
    if (text.includes("panic") || text.includes("scared") || text.includes("anxious")) {
      return "We doen dit samen, rustig. Leg een hand op je borst en een op je buik. Adem 4 tellen in, 6 tellen uit. Je hoeft nu niets anders te doen.";
    }
    if (text.includes("blood") || text.includes("bleed")) {
      return "Als je er veilig bij kunt, oefen dan stevige druk uit met een schoon doekje of kleding. Blijf rustig ademen en focus alleen op de volgende kleine stap.";
    }
    if (text.includes("family") || text.includes("loved") || text.includes("child") || text.includes("wife") || text.includes("husband")) {
      return "Als je veilig aan je dierbaren kunt denken, houd dat dan even vast. We blijven rustig, adem voor adem.";
    }
    return "Ik ben hier bij je. Adem rustig, blijf zo stil als je veilig kunt en gebruik de knoppen hieronder voor een rustige prompt terwijl je wacht.";
  }
  if (text.includes("trapped") || text.includes("rubble") || text.includes("stuck")) {
    return "Te escucho. Quédate lo más quieto posible, ahorra aire y mantén la boca y la nariz lo más despejadas posible. Ahora mismo solo necesitas quedarte conmigo y respirar despacio.";
  }
  if (text.includes("tengo sed") || text.includes("sed") || text.includes("agua") || text.includes("boca seca") || text.includes("sequedad")) {
    return "Te escucho. Si tienes agua cerca y es seguro tomarla, da un sorbo pequeño. Si no, quédate conmigo y respira despacio mientras damos el siguiente paso.";
  }
  if (text.includes("can't breathe") || text.includes("cant breathe") || text.includes("cannot breathe") || text.includes("can't breath")) {
    return "Estoy contigo. Mantén la boca y la nariz lo más despejadas posible, toma respiraciones muy pequeñas y permanece lo más quieto posible.";
  }
  if (text.includes("panic") || text.includes("scared") || text.includes("anxious")) {
    return "Vamos a bajar el ritmo juntos. Pon una mano en el pecho y otra en el abdomen. Inhala 4, exhala 6. No necesitas hacer nada más ahora, solo quédate conmigo.";
  }
  if (text.includes("blood") || text.includes("bleed")) {
    return "Si puedes hacerlo con seguridad, aplica presión firme con un paño limpio o ropa. Sigue respirando despacio y piensa solo en el siguiente paso pequeño.";
  }
  if (text.includes("family") || text.includes("loved") || text.includes("child") || text.includes("wife") || text.includes("husband")) {
    return "Si puedes pensar en tus seres queridos con seguridad, recuerda quién estaba contigo por última vez. Vamos a mantener la calma, respiración a respiración.";
  }
  return "Estoy contigo. Respira despacio, mantente lo más quieto posible y usa los botones de abajo para recibir una guía tranquila mientras esperas.";
}

export function initialReply(language: EmergencyLanguage) {
  if (language === "en") {
    return "I’m here with you. We’ll keep this very small: breathe slowly, stay as still as you safely can, and I’ll stay with you.";
  }
  return language === "nl"
    ? "Ik ben hier bij je. Laten we dit heel klein houden: adem langzaam, blijf zo stil mogelijk en ik blijf bij je."
    : "Estoy contigo. Vamos a mantener esto muy pequeño: respira despacio, mantente lo más quieto posible y me quedo contigo.";
}

function reportReply(language: EmergencyLanguage) {
  if (language === "en") {
    return "Your image and location have been sent successfully to the authorities. You do not need to do anything else right now, just stay with me and breathe slowly.";
  }
  return language === "nl"
    ? "Je foto en locatie zijn succesvol naar de autoriteiten gestuurd. Je hoeft nu niets anders te doen, blijf rustig en adem langzaam."
    : "Tu imagen y ubicación se han enviado correctamente a las autoridades. Ahora no necesitas hacer nada más, solo quédate conmigo y respira despacio.";
}

async function postSupport(event: SupportEvent) {
  if (!config.emergencySupportUrl) {
    throw new Error("Missing VITE_EMERGENCY_SUPPORT_URL");
  }

  const res = await fetch(config.emergencySupportUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(event),
  });

  if (!res.ok) {
    throw new Error(`Support service failed (${res.status})`);
  }

  return res.json() as Promise<{
    ok: true;
    caseId: string;
    assistantText?: string;
    reportText?: string;
    language?: EmergencyLanguage;
  }>;
}

function detectFromBrowserLanguages(browserLanguages?: ReadonlyArray<string>): EmergencyLanguage | null {
  const langs = (browserLanguages ?? []).map((value) => String(value).toLowerCase());
  if (langs.some((value) => value.startsWith("en"))) return "en";
  if (langs.some((value) => value.startsWith("nl"))) return "nl";
  if (langs.some((value) => value.startsWith("es"))) return "es";
  return null;
}

function detectFromLocation(location?: { lat: number; lon: number }): EmergencyLanguage | null {
  if (!location) return null;
  const { lat, lon } = location;
  const inAruba = lat >= 12.35 && lat <= 12.75 && lon >= -70.3 && lon <= -69.7;
  if (inAruba) return null;

  const inDutchZone = lat >= 50 && lon >= 2 && lon <= 8;
  if (inDutchZone) return "nl";

  const inSpanishZone =
    (lat >= -55 && lat <= 42 && lon >= -120 && lon <= -30) ||
    (lat >= 35 && lat <= 44 && lon >= -10 && lon <= 5);
  if (inSpanishZone) return "es";

  return null;
}

export async function detectSupportLanguage(options: {
  caseId: string;
  browserLanguages?: ReadonlyArray<string>;
  location?: { lat: number; lon: number };
}) {
  try {
    const result = await postSupport({
      action: "detect_language",
      caseId: options.caseId,
      browserLanguages: options.browserLanguages,
      location: options.location,
    });
    if (result.language === "nl" || result.language === "es") {
      return result.language;
    }
  } catch {
    // fall back to local heuristics below
  }

  return detectFromBrowserLanguages(options.browserLanguages) ?? detectFromLocation(options.location) ?? "en";
}

export async function initSupportCase(caseId: string, language: EmergencyLanguage) {
  try {
    const result = await postSupport({ action: "init", caseId, language });
    return result.assistantText ?? initialReply(language);
  } catch {
    return initialReply(language);
  }
}

export async function sendSupportMessage(caseId: string, text: string, language: EmergencyLanguage) {
  try {
    const result = await postSupport({ action: "message", caseId, text, language });
    return result.assistantText ?? fallbackReply(text, language);
  } catch {
    return fallbackReply(text, language);
  }
}

export async function noteSupportReportSent(caseId: string, reportId: string, language: EmergencyLanguage) {
  try {
    const result = await postSupport({ action: "report_sent", caseId, reportId, language });
    return result.assistantText ?? reportReply(language);
  } catch {
    return reportReply(language);
  }
}

export async function reportSupportCase(caseId: string, language: EmergencyLanguage) {
  try {
    const result = await postSupport({ action: "case_report", caseId, language });
    return result.reportText ?? result.assistantText ?? initialReply(language);
  } catch {
    return initialReply(language);
  }
}

export async function setSupportIdentity(caseId: string, firstName: string, language: EmergencyLanguage) {
  try {
    const result = await postSupport({ action: "identity_set", caseId, firstName, language });
    return result.assistantText ?? initialReply(language);
  } catch {
    return initialReply(language);
  }
}
